import { QueryClient, QueryFunction, useQuery, UseQueryResult } from "@tanstack/react-query";

async function throwIfResNotOk(res: Response) {
  // Accept 2xx status codes (200-299) - res.ok is true for all 2xx codes including 202
  // But explicitly allow 202 Accepted for async processing
  const isOk = res.ok || res.status === 202 || (res.status >= 200 && res.status < 300);
  
  if (!isOk) {
    const text = (await res.text()) || res.statusText;
    
    // Don't clear token on 401/403 - let the auth system handle it
    // Only log auth errors in development mode to reduce console noise
    if (res.status === 401 || res.status === 403) {
      // In Vite, use import.meta.env.DEV for development mode check
      if (import.meta.env.DEV) {
        console.log(`[API] Authentication error: ${res.status} - ${text}`);
      }
    }
    
    throw new Error(`${res.status}: ${text}`);
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  // Handle FormData specially - let browser set Content-Type with boundary
  const isFormData = data instanceof FormData;
  
  // Get JWT token from localStorage
  const token = localStorage.getItem('authToken');
  
  const headers: Record<string, string> = {};
  if (!isFormData && data) {
    headers["Content-Type"] = "application/json";
  }
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  
  // Add timeout for uploads (120 seconds for large files)
  const timeout = isFormData ? 120000 : 30000;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    console.log(`[API] Request timeout after ${timeout / 1000} seconds for ${method} ${url}`);
    controller.abort();
  }, timeout);
  
  try {
    console.log(`[API] Making ${method} request to ${url}`, { hasToken: !!token, isFormData, timeout: timeout / 1000 });
    const res = await fetch(url, {
      method,
      headers,
      body: isFormData ? data : (data ? JSON.stringify(data) : undefined),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    console.log(`[API] Response received for ${method} ${url}:`, res.status, res.statusText);
    await throwIfResNotOk(res);
    return res;
  } catch (error: any) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      console.error(`[API] Request aborted/timeout for ${method} ${url}`);
      throw new Error(`Request timeout after ${timeout / 1000} seconds`);
    }
    console.error(`[API] Request error for ${method} ${url}:`, error);
    throw error;
  }
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    // Get JWT token from localStorage
    const token = localStorage.getItem('authToken');
    
    const headers: Record<string, string> = {};
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    
    const res = await fetch(queryKey.join("/") as string, {
      headers,
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});

// System Health Check Types
export interface SystemHealthCheck {
  service: string;
  status: 'healthy' | 'degraded' | 'unhealthy';
  latency?: number;
  message?: string;
}

export interface SystemHealthResponse {
  status: 'healthy' | 'degraded' | 'unhealthy';
  timestamp: string;
  totalLatency: number;
  checks: SystemHealthCheck[];
  version: string;
  environment: string;
}

/**
 * useSystemHealth - React Query hook for system health monitoring
 * 
 * Fetches comprehensive system status including:
 * - Database connectivity (Supabase)
 * - AI services (Claude, OpenAI)
 * - Document storage status
 * - Vector store (embeddings) status
 * 
 * @param options.enabled - Whether to enable automatic fetching (default: true)
 * @param options.refetchInterval - Auto-refresh interval in ms (default: 30000)
 * 
 * @example
 * ```tsx
 * const { data, isLoading, isError, refetch } = useSystemHealth();
 * 
 * if (isLoading) return <span>Checking system...</span>;
 * if (isError) return <span>System check failed</span>;
 * 
 * return (
 *   <div>
 *     <span>Status: {data.status}</span>
 *     {data.checks.map(check => (
 *       <div key={check.service}>
 *         {check.service}: {check.status} ({check.latency}ms)
 *       </div>
 *     ))}
 *   </div>
 * );
 * ```
 */
export function useSystemHealth(options?: {
  enabled?: boolean;
  refetchInterval?: number | false;
}): UseQueryResult<SystemHealthResponse> {
  const { enabled = true, refetchInterval = 30000 } = options || {};
  
  return useQuery<SystemHealthResponse>({
    queryKey: ['system-health'],
    queryFn: async () => {
      const response = await fetch('/api/system-check');
      if (!response.ok) {
        throw new Error(`System check failed: ${response.status}`);
      }
      return response.json();
    },
    enabled,
    refetchInterval,
    staleTime: 10000, // Consider data stale after 10 seconds
    retry: 1, // Only retry once for health checks
    refetchOnWindowFocus: true, // Refresh when user returns to tab
  });
}
